import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
import { type LlmModel, type LlmVendor, useModelCatalogue } from "@/lib/llmModels";

const VENDORS: LlmVendor[] = ["Claude", "GPT", "Gemini"];

const TIER_LABELS: Record<LlmModel["tier"], string> = {
  premium: "Premium",
  standard: "Standard",
  fast: "Fast",
};

// Searchable list over the live OpenRouter catalogue (~85 Claude / GPT / Gemini
// models), grouped by vendor. Falls back to the static list if the fetch fails.
// Render inline where there is room (the matchups settings sheet) or wrap it
// in ModelPickerButton for a compact trigger.
const ModelPicker = ({ value, onChange }: { value: string; onChange: (id: string) => void }) => {
  const { models, isLive } = useModelCatalogue();

  return (
    <div className="space-y-1">
      <Command className="rounded-md border">
        <CommandInput placeholder="Search models…" />
        <CommandList className="max-h-[50vh]">
          <CommandEmpty>No model matches.</CommandEmpty>
          {VENDORS.map((vendor) => {
            const items = models.filter((m) => m.vendor === vendor);
            if (items.length === 0) return null;
            return (
              <CommandGroup key={vendor} heading={vendor}>
                {items.map((m) => (
                  <CommandItem key={m.id} value={`${m.label} ${m.id}`} onSelect={() => onChange(m.id)}>
                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value === m.id ? "opacity-100" : "opacity-0")} />
                    <span className="min-w-0 flex-1 truncate">{m.label}</span>
                    <span className="ml-2 shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-medium uppercase text-muted-foreground">
                      {TIER_LABELS[m.tier]}
                    </span>
                    <span className="ml-2 shrink-0 text-[10px] text-muted-foreground">
                      ${m.inPerM}/${m.outPerM}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })}
        </CommandList>
      </Command>
      {!isLive && (
        <p className="text-[11px] text-muted-foreground">Live catalogue unavailable — showing a built-in list.</p>
      )}
    </div>
  );
};

export default ModelPicker;

// Compact trigger that opens the picker in a drawer. Closes on selection.
export const ModelPickerButton = ({
  value,
  onChange,
  disabled,
  title,
}: {
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  title?: string;
}) => {
  const [open, setOpen] = useState(false);
  const { models } = useModelCatalogue();
  const label = models.find((m) => m.id === value)?.label ?? value;

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="flex max-w-full items-center gap-1 rounded-md border border-input px-2 py-1 text-left text-xs font-medium transition-colors hover:bg-muted/50 disabled:opacity-50"
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
      </button>
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{title ?? "Choose a model"}</DrawerTitle>
            <DrawerDescription>Any current Claude, GPT or Gemini model on OpenRouter.</DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-6">
            <ModelPicker
              value={value}
              onChange={(id) => {
                onChange(id);
                setOpen(false);
              }}
            />
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};
